import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useContext, useEffect, useRef } from "react";

import { AccountContext } from "@/providers/account";
import dayjs from "@/utils/date";
import { createStorageKey } from "@/utils/storage";

import { useIntercom } from "./useIntercom";

export const INTERCOM_ONBOARDING_CHECKLIST = 38964974;

const NEW_ACCOUNT_DAYS = 21;
const CHECKLIST_DELAY_MS = 10_000;

const checklistLastTriggeredAtom = atomWithStorage<string | null>(
  createStorageKey("onboarding-checklist-last-triggered"),
  null,
);

/**
 * Auto-opens Intercom's onboarding guide once, for accounts young enough to
 * still want it. `ready` gates on the messenger having been identified — the
 * checklist cannot start before Intercom knows who is asking.
 */
export const useStartIntercomChecklistForNewAccounts = (ready: boolean) => {
  const [lastTriggered, setLastTriggered] = useAtom(checklistLastTriggeredAtom);
  const account = useContext(AccountContext)?.account;
  const triggered = useRef(false);
  const { startChecklist } = useIntercom();

  useEffect(() => {
    if (!ready || !account || triggered.current) return;

    //only autodisplay checklist once
    if (lastTriggered) return;

    const accountCreated = dayjs(account.createdAt);
    if (accountCreated.isBefore(dayjs().subtract(NEW_ACCOUNT_DAYS, "day"))) {
      return;
    }

    //show checklist (after a short delay to avoid users closing it right away)
    triggered.current = true;
    const timeout = setTimeout(() => {
      startChecklist(INTERCOM_ONBOARDING_CHECKLIST);
      setLastTriggered(dayjs().toISOString());
    }, CHECKLIST_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [account, lastTriggered, ready, setLastTriggered, startChecklist]);
};
